import React, { forwardRef } from 'react';
import cn from 'classnames';
import { useId } from '@reach/auto-id';

// Redeclare forwardRef to support generics:
// https://fettblog.eu/typescript-react-generic-forward-refs/#option-3%3A-augment-forwardref
declare module "react" {
    function forwardRef<T, P = {}>(
        render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
    ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

export interface IMultiSelectOptionProps<T> {
    id: string,
    index: number,
    isFocused: boolean,
    isSelected: boolean,
    option: T,
    onMouseEnter: () => void,
    onSelect: () => void,
    onUnselect: () => void,
    ref?: React.Ref<HTMLDivElement>,
    role?: string,
}

function _MultiSelectOption<T>({
    id,
    index,
    isFocused,
    isSelected,
    option,
    role,
    onMouseEnter,
    onSelect,
    onUnselect,
}: IMultiSelectOptionProps<T>, ref: React.Ref<HTMLDivElement>) {
    const labelId = useId();

    function onToggle(e: React.MouseEvent<HTMLElement>) {
        isSelected ? onUnselect() : onSelect();
    }

    return (
        <div
            aria-labelledby={labelId}
            aria-selected={isSelected}
            className={cn("multiSelectOption", {
                focused: isFocused,
                selected: isSelected,
            })}
            id={id}
            onClick={onToggle}
            onMouseEnter={onMouseEnter}
            ref={ref}
            role={role}
        >
            <div className="multiSelectOption_checkboxWrap">
                <input
                    // We don't need the screenreader to announce the checkbox.
                    aria-hidden={true}
                    checked={isSelected}
                    className="multiSelectOption_checkbox"
                    // We're passing an empty handler to make React happy.
                    // The actual click is handled by the list item
                    onChange={() => undefined}
                    // We're doing custom focus management, so we don't want this in the tab order.
                    tabIndex={-1}
                    type="checkbox"
                />
            </div>
            <div className="multiSelectOption_label" id={labelId}>
                {option + ''}
            </div>
        </div>
    );
}

export const MultiSelectOption = forwardRef(_MultiSelectOption);